import { change, Field, reduxForm } from "redux-form";
import InputRedux from "components/molecules/InputRedux";
import SelectRedux from "components/molecules/SelectRedux";
import { email, phone, required, requiredPhone } from "utils/validate.util";
import PhoneInputRedux from "components/molecules/PhoneInputRedux";
import { requiredLabel } from "components/atoms/RequiredMark";
import { TEXTAREA_ROWS } from "constants/textarea";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import PersistReduxForm from "components/organisms/PersistReduxForm";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ObjectService from "utils/object.util";
import LocalStorage from "utils/localstorage.util";
import {
  getStoredPartnerUser,
  partnerUserPhoneFieldValue,
} from "utils/partnerUser.util";
import FlightNumber from "./FlightNumber";
import "./AdditionalInfoForm.scss";

function AdditionalInfoForm({ handleSubmit, form }: any) {
  const { t } = useTranslation("translation");
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  const title = useAppSelector((state) => state.form?.[form]?.values?.title);

  /* "" is a real choice, not an empty state — some passengers would rather
     the chauffeur's sign carried no title at all. Same order as usernext's
     titleOptions (Mr., Ms., No title); usernext stores "No title" as a
     NO_TITLE sentinel that its http.service rewrites to "", this form keeps
     storing "" directly. */
  const titleOptions = [
    { value: "Mr.", label: String(t("mr")) },
    { value: "Ms.", label: String(t("ms")) },
    { value: "", label: String(t("noTitle")) },
  ];
  const [signPreviewOpen, setSignPreviewOpen] = useState(false);
  const first_name = useAppSelector(
    (state) => state.form?.[form]?.values?.first_name
  );
  const pickup_sign = useAppSelector(
    (state) => state.form?.[form]?.values?.pickup_sign
  );

  const searchTripFormValues = useAppSelector(
    (state) => state.form?.PickUpForm?.values
  );
  /* The flight number is asked for an airport PICKUP, exactly as in
     yelo-usernext's checkout (its pickup_type is "airport" only for an
     airport pickup). The saved search remembers it (BookingRouteNames),
     because the live PickUpForm is empty after a reload and the field used
     to vanish. Searches saved before that flag fall back to pickup_type. */
  const savedPickupAirport = LocalStorage.getItem("BookingRouteNames")
    ?.pickupAirport;
  const airport = Boolean(
    searchTripFormValues?.pickup?.details?.airport ??
      (typeof savedPickupAirport === "boolean"
        ? savedPickupAirport
        : LocalStorage.getItem("BookingFormPayload")?.pickup_type === "airport")
  );

  /* Not an airport booking: drop any flight number left in the persisted
     form by an earlier airport trip. The field is hidden here, so the
     reader could neither see nor clear it, and it rode along with the
     booking. */
  useEffect(() => {
    if (airport) return;
    dispatch(change(form, "flight_number", ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airport]);

  useEffect(() => {
    const storedValues = LocalStorage.getItem(form);
    if (storedValues) {
      Object.keys(storedValues).forEach((key) => {
        const value = storedValues[key];
        /* An earlier airport trip's flight number is not this booking's. */
        if (key === "flight_number" && !airport) return;
        /* "" is the "No title" choice and has to be restored as-is. */
        const keepEmptyTitle = key === "title" && value === "";
        if (
          keepEmptyTitle ||
          (value !== undefined && value !== null && value !== "")
        ) {
          dispatch(change(form, key, value));
        }
      });
    }

    const titleAlreadyChosen =
      storedValues &&
      Object.prototype.hasOwnProperty.call(storedValues, "title");

    if (!titleAlreadyChosen) {
      /* undefined means "never set"; "" means the passenger chose no title. */
      dispatch(change(form, "title", "Mr."));
    }

    const partnerUser = getStoredPartnerUser() || user;
    if (!partnerUser) return;

    if (partnerUser.first_name) {
      dispatch(change(form, "first_name", partnerUser.first_name));
    }
    if (partnerUser.last_name) {
      dispatch(change(form, "last_name", partnerUser.last_name));
    }
    if (partnerUser.email) {
      dispatch(change(form, "email", partnerUser.email));
    }
    if (partnerUser.title && !titleAlreadyChosen) {
      dispatch(change(form, "title", partnerUser.title));
    }

    const phoneField = partnerUserPhoneFieldValue(partnerUser);
    if (phoneField) {
      dispatch(change(form, "phone", phoneField));
    }

    dispatch(
      change(form, "pickup_sign", ObjectService.getFullName(partnerUser, false))
    );
  }, [user, dispatch, form]);

  return (
    <div>
      <form onSubmit={handleSubmit} className="yp-passenger">
        <p className="yp-passenger__glabel">{t("whoIsTravelling")}</p>
        {/* A dropdown, not a segmented pair. Two buttons spent a whole row on
            a field most people never change, and the set could not grow — a
            title that is neither Mr nor Ms had nowhere to go, including the
            common case of preferring none at all. */}
        <div className="yp-passenger__field">
          {/* Always holds a choice (Mr. by default, or "No title"), so it is
              marked like usernext's required title. SelectRedux has no
              `required` prop here, so the mark goes on the label itself. */}
          <Field
            name="title"
            label={requiredLabel(t("title"), true)}
            component={SelectRedux}
            SelectProps={{ options: titleOptions }}
          />
        </div>

        <div className="yp-passenger__two">
          <Field
            name="first_name"
            label={t("firstName")}
            component={InputRedux}
            validate={[required]}
            required
          />
          <Field
            name="last_name"
            label={t("lastName")}
            component={InputRedux}
            validate={[required]}
            required
          />
        </div>

        {airport && (
          <div className="yp-passenger__field">
            <Field
              name="flight_number"
              label={t("flightNumber")}
              placeholder={t("flight_number_example")}
              InputLabelProps={{ shrink: true }}
              component={FlightNumber}
              validate={[required]}
              required
            />
          </div>
        )}

        <div className="yp-passenger__field">
          {/* Optional, as in usernext's guest form. An empty email falls back
              to the partner user's in resolvePartnerPassengerEmail. */}
          <Field
            name="email"
            label={t("email")}
            placeholder={t("yp_guest_email_placeholder")}
            InputLabelProps={{ shrink: true }}
            component={InputRedux}
            validate={[email]}
          />
        </div>

        <div className="yp-passenger__field">
          {/* The design labels this "Phone"; the app-wide string is "Phone Number". */}
          <Field
            name="phone"
            label={t("yp_phone")}
            component={PhoneInputRedux}
            validate={[requiredPhone, phone]}
            required
          />
        </div>

        {/* The preview used to sit under the field as a permanent panel,
            repeating a value the field already shows two rows up. It is the
            answer to one question — what will the board actually say — so it
            is asked for, not pushed. */}
        <p className="yp-passenger__glabel yp-passenger__glabel--info">
          {t("forYourChauffeur")}
          <button
            type="button"
            className="yp-passenger__info"
            aria-label={String(t("chauffeurWillHold"))}
            onClick={() => setSignPreviewOpen(true)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor"
                strokeWidth="1.7" />
              <path d="M12 11v5" fill="none" stroke="currentColor"
                strokeWidth="1.7" strokeLinecap="round" />
              <circle cx="12" cy="7.9" r="1.05" fill="currentColor" />
            </svg>
          </button>
        </p>
        <div className="yp-passenger__field">
          <Field
            name="pickup_sign"
            label={t("yp_passenger_name_label")}
            placeholder={t("yp_passenger_name_placeholder")}
            InputLabelProps={{ shrink: true }}
            component={InputRedux}
          />
        </div>

        <div className="yp-passenger__field">
          <Field
            name="notes"
            label={t("yp_chauffeur_notes_label")}
            placeholder={t("yp_chauffeur_notes_placeholder")}
            InputLabelProps={{ shrink: true }}
            component={InputRedux}
            InputProps={{ multiline: true, rows: TEXTAREA_ROWS }}
          />
        </div>

        <div className="yp-passenger__dock">
          <button type="submit" className="yp-passenger__submit">
            {t("continue")}
          </button>
        </div>
      </form>

      {signPreviewOpen && (
        <div
          className="yp-sign-modal"
          role="dialog"
          aria-modal="true"
          aria-label={String(t("chauffeurWillHold"))}
        >
          <div
            className="yp-sign-modal__scrim"
            onClick={() => setSignPreviewOpen(false)}
          />
          <div className="yp-sign-modal__panel">
            <p className="yp-sign-modal__label">{t("chauffeurWillHold")}</p>
            <p className="yp-sign-modal__board">
              {pickup_sign || `${title} ${first_name ?? ""}`.trim() || "—"}
            </p>
            <button
              type="button"
              className="yp-sign-modal__close"
              onClick={() => setSignPreviewOpen(false)}
            >
              {t("close")}
            </button>
          </div>
        </div>
      )}
      <PersistReduxForm form={form} />
    </div>
  );
}

export default reduxForm({
  form: "AdditionalInfoForm",
  destroyOnUnmount: false,
})(AdditionalInfoForm);
